import { FieldOptionType, GarAddressType } from "./Types";
import ValidatorsFactory, { Validator } from "src/Utils/Validation";

/** Модель данных ГАР */
export class GarDataModel {

	visibilityMask: string = '-';
	requiredMask: string = '';
	municipalSplit: boolean = false;
	isUseSocr: boolean = false;

	validationEnabled: boolean = false;

	region: GarFieldDataModel;
	state: GarFieldDataModel;
	municipalArea: GarFieldDataModel;
	settlement: GarFieldDataModel;
	city: GarFieldDataModel;
	cityDistrict: GarFieldDataModel;
	street: GarFieldDataModel;
	additionalTerritory: GarFieldDataModel;
	streetOnAdditionalTerritory: GarFieldDataModel;
	houseAo: GarFieldDataModel;
	house: GarFieldDataModel;
	corpus: GarFieldDataModel;
	building: GarFieldDataModel;
	dwelling: GarFieldDataModel;
	flat: GarFieldDataModel;
	postalIndex: GarFieldDataModel;

	constructor( mask: string ) {

		this.prepareMask( mask )

		this.region = new GarFieldDataModel( this, GarFieldCodes.regionCode );
		this.state = new GarFieldDataModel( this, GarFieldCodes.stateCode );
		this.municipalArea = new GarFieldDataModel( this, GarFieldCodes.municipalCode );
		this.settlement = new GarFieldDataModel( this, GarFieldCodes.settlementCode );
		this.city = new GarFieldDataModel( this, GarFieldCodes.cityCode );
		this.cityDistrict = new GarFieldDataModel( this, GarFieldCodes.microDistrictCode );
		this.street = new GarFieldDataModel( this, GarFieldCodes.streetCode );
		this.additionalTerritory = new GarFieldDataModel( this, GarFieldCodes.addTerCode );
		this.streetOnAdditionalTerritory = new GarFieldDataModel( this, GarFieldCodes.strAddTerCode );
		this.houseAo = new GarFieldDataModel( this, GarFieldCodes.houseAoCode );
		this.house = new GarFieldDataModel( this, GarFieldCodes.houseCode );
		this.corpus = new GarFieldDataModel( this, GarFieldCodes.corpusCode );
		this.building = new GarFieldDataModel( this, GarFieldCodes.buildingCode );
		this.dwelling = new GarFieldDataModel( this, GarFieldCodes.dwellingCode );
		this.flat = new GarFieldDataModel( this, GarFieldCodes.flatCode );
		this.postalIndex = new GarFieldDataModel( this, GarFieldCodes.indexCode );
		//валидатор для индекса
		const indexValidators = [];
		if ( this.postalIndex.required )
			indexValidators.push(ValidatorsFactory.getRequiredValidator());
		indexValidators.push(ValidatorsFactory.getRegexValidator(/^\d{6}$|^$/, 'Некорректное значение'));
		this.postalIndex.validator = ValidatorsFactory.getComplexValidatorForValidators(indexValidators);
	};

	prepareMask = ( mask: string ) => {
		this.isUseSocr = mask.indexOf("=") >= 0;
		const index = mask.indexOf("!");
		this.municipalSplit = mask.indexOf("?") >= 0;
		if (index >= 0) {
			this.requiredMask = mask.substring(index, mask.length);
		}
		if (mask.lastIndexOf("+", 0) === 0 || mask.lastIndexOf("-", 0) === 0) {
			this.visibilityMask = mask.substring(0, (index >= 0 ? index : mask.length));
		}
	};

	/** Возвращает массив с моделями данных ГАР-полей */
	getFields = (): GarFieldDataModel[] => {
		return [
			this.region,
			this.state,
			this.municipalArea,
			this.settlement,
			this.city,
			this.cityDistrict,
			this.street,
			this.additionalTerritory,
			this.streetOnAdditionalTerritory,
			this.houseAo,
			this.house,
			this.corpus,
			this.building,
			this.dwelling,
			this.flat,
			this.postalIndex
		];
	};

	/**
	 * Обновляет модель данных ГАР-полей по объекту ГАР-адреса
	 * @param value ГАР адрес
	 * @returns Обновленная модель данных
	 */
	onUpdateByAddress = ( value: GarAddressType | null ) => {
		if ( !value ) this.clear();
		else {
			this.region.selectedAoGuid = value.regionAoGuid;
			this.region.text = value.regionName;
			this.region.kladrId = value.regionKladrCode;
			this.region.shortName = value.regionShortName;
			this.region.formalName = value.regionFormalName;
			this.region.okato = value.regionOkato;
			this.region.oktmo = value.regionOktmo;
			this.region.regionCode = value.regionCode;
			this.region.aoid = value.regionAoId;

			this.state.selectedAoGuid = value.stateAoGuid;
			this.state.text = value.stateName;
			this.state.kladrId = value.stateKladrCode;
			this.state.shortName = value.stateShortName;
			this.state.formalName = value.stateFormalName;
			this.state.okato = value.stateOkato;
			this.state.oktmo = value.stateOktmo;
			this.state.aoid = value.stateAoId;

			this.municipalArea.selectedAoGuid = value.municipalAreaAoGuid;
			this.municipalArea.text = value.municipalAreaName;
			this.municipalArea.shortName = value.municipalAreaShortName;
			this.municipalArea.formalName = value.municipalAreaFormalName;
			this.municipalArea.officialName = value.officialName;
			this.municipalArea.oktmo = value.municipalAreaOktmo;
			this.municipalArea.aoid = value.municipalAreaAoId;

			this.settlement.selectedAoGuid = value.settlementAoGuid;
			this.settlement.text = value.settlementName;
			this.settlement.shortName = value.settlementShortName;
			this.settlement.formalName = value.settlementFormalName;
			this.settlement.officialName = value.officialName;
			this.settlement.oktmo = value.settlementOktmo;
			this.settlement.aoid = value.settlementAoId;

			this.city.selectedAoGuid = value.cityAoGuid;
			if ( !value.cityAoGuid && ( this.isFederalCitySelected() || value.stateShortName === 'г' ) ) {
				this.city.text = '—';
			} 
			else {
				this.city.text = value.cityName;
			}
			this.city.kladrId = value.cityKladrCode;
			this.city.shortName = value.cityShortName;
			this.city.formalName = value.cityFormalName;
			this.city.okato = value.cityOkato;
			this.city.oktmo = value.cityOktmo;
			this.city.aoid = value.cityAoId;

			this.cityDistrict.selectedAoGuid = value.cityDistrictAoGuid;
			this.cityDistrict.text = value.cityDistrictName;
			this.cityDistrict.kladrId = value.cityDistrictKladrCode;
			this.cityDistrict.shortName = value.cityDistrictShortName;
			this.cityDistrict.formalName = value.cityDistrictFormalName;
			this.cityDistrict.okato = value.cityDistrictOkato;
			this.cityDistrict.oktmo = value.cityDistrictOktmo;
			this.cityDistrict.aoid = value.cityDistrictAoId;

			this.street.selectedAoGuid = value.streetAoGuid;
			if (value.streetAoGuid == null && value.houseGuid != null) {
				this.street.text = '—';
			}
			else {
				this.street.text = value.streetName;
			}
			this.street.kladrId = value.streetKladrCode;
			this.street.shortName = value.streetShortName;
			this.street.formalName = value.streetFormalName;
			this.street.aoid = value.streetAoId;

			this.additionalTerritory.selectedAoGuid = value.additionalTerritoryAoGuid;
			this.additionalTerritory.text = value.additionalTerritoryName;
			this.additionalTerritory.shortName = value.additionalTerritoryShortName;
			this.additionalTerritory.formalName = value.additionalTerritoryFormalName;
			this.additionalTerritory.aoid = value.additionalTerritoryAoId;

			this.streetOnAdditionalTerritory.selectedAoGuid = value.streetOnAdditionalTerritoryAoGuid;
			this.streetOnAdditionalTerritory.text = value.streetOnAdditionalTerritoryName;
			this.streetOnAdditionalTerritory.shortName = value.streetOnAdditionalTerritoryShortName;
			this.streetOnAdditionalTerritory.formalName = value.streetOnAdditionalTerritoryFormalName;
			this.streetOnAdditionalTerritory.aoid = value.streetOnAdditionalTerritoryAoId;

			this.houseAo.selectedAoGuid = value.houseGuid;
			this.houseAo.text = value.house;
			this.houseAo.kladrId = value.houseKladrId ?? null;
			this.houseAo.shortName = value.houseType ?? null;
			this.houseAo.formalName = value.house;
			this.house.text = value.house;

			this.flat.text = value.flat;
			this.flat.flatGuid = value.flatGuid;

			this.postalIndex.text = value.postalIndex;
			this.corpus.text = value.corpuse;
			this.building.text = value.building;
			this.dwelling.text = value.dwelling;
		}
		return this;
	};

	/** функция для получения результата */
	getValue = () => {
		const result: GarAddressType = {
			regionAutoCode: null,
			stateAreaCode: null,
			municipalAreaAreaCode: null,
			settlementAreaCode: null,
			cityCityCode: null,
			cityPlaceCode: null,
			cityDistrictCtarCode: null,
			streetStreetCode: null,
			additionalTerritoryExtrCode: null,
			streetOnAdditionalTerritorySextCode: null,
			officialName: null,
			houseKladrId: null,
			houseType: null,

			//регион
			regionAoGuid: this.region.selectedAoGuid,
			regionName: this.region.text,
			regionShortName: this.region.shortName,
			regionFormalName: this.region.formalName,
			regionKladrCode: this.region.kladrId,
			regionOkato: this.region.okato,
			regionOktmo: this.region.oktmo,
			regionCode: this.region.regionCode,
			regionAoId: this.region.aoid,

			//Район
			stateAoGuid: this.state.selectedAoGuid,
			stateName: this.state.text,
			stateShortName: this.state.shortName,
			stateFormalName: this.state.formalName,
			stateKladrCode: this.state.kladrId,
			stateOkato: this.state.okato,
			stateOktmo: this.state.oktmo,
			stateAoId: this.state.aoid,

			//Муниципальный район
			municipalAreaAoGuid: this.municipalArea.selectedAoGuid,
			municipalAreaName: this.municipalArea.text,
			municipalAreaShortName: this.municipalArea.shortName,
			municipalAreaFormalName: this.municipalArea.formalName,
			municipalAreaOfficialName: this.municipalArea.officialName,
			municipalAreaOktmo: this.municipalArea.oktmo,
			municipalAreaAoId: this.municipalArea.aoid,

			//Поселение
			settlementAoGuid: this.settlement.selectedAoGuid,
			settlementName: this.settlement.text,
			settlementShortName: this.settlement.shortName,
			settlementFormalName: this.settlement.formalName,
			settlementOfficialName: this.settlement.officialName,
			settlementOktmo: this.settlement.oktmo,
			settlementAoId: this.settlement.aoid,

			//Город (Населенный пункт)
			cityAoGuid: this.city.selectedAoGuid,
			cityName: this.city.text,
			cityShortName: this.city.shortName,
			cityFormalName: this.city.formalName,
			cityKladrCode: this.city.kladrId,
			cityOkato: this.city.okato,
			cityOktmo: this.city.oktmo,
			cityAoId: this.city.aoid,

			//Район города
			cityDistrictAoGuid: this.cityDistrict.selectedAoGuid,
			cityDistrictName: this.cityDistrict.text,
			cityDistrictShortName: this.cityDistrict.shortName,
			cityDistrictFormalName: this.cityDistrict.formalName,
			cityDistrictKladrCode: this.cityDistrict.kladrId,
			cityDistrictOkato: this.cityDistrict.okato,
			cityDistrictOktmo: this.cityDistrict.oktmo,
			cityDistrictAoId: this.cityDistrict.aoid,

			//Улица
			streetAoGuid: this.street.selectedAoGuid,
			streetName: this.street.text,
			streetShortName: this.street.shortName,
			streetFormalName: this.street.formalName,
			streetKladrCode: this.street.kladrId,
			streetAoId: this.street.aoid,

			additionalTerritoryAoGuid: this.additionalTerritory.selectedAoGuid,
			additionalTerritoryName: this.additionalTerritory.text,
			additionalTerritoryShortName: this.additionalTerritory.shortName,
			additionalTerritoryFormalName: this.additionalTerritory.formalName,
			additionalTerritoryAoId: this.additionalTerritory.aoid,

			streetOnAdditionalTerritoryAoGuid: this.streetOnAdditionalTerritory.selectedAoGuid,
			streetOnAdditionalTerritoryName: this.streetOnAdditionalTerritory.text,
			streetOnAdditionalTerritoryShortName: this.streetOnAdditionalTerritory.shortName,
			streetOnAdditionalTerritoryFormalName: this.streetOnAdditionalTerritory.formalName,
			streetOnAdditionalTerritoryAoId: this.additionalTerritory.aoid,

			//Дом
			houseGuid: this.houseAo.selectedAoGuid,
			house: this.house.text,

			//Корпус
			corpuse: this.corpus.text,

			//Строение
			building: this.building.text,

			//Квартира
			flatGuid: this.flat.flatGuid,
			flat: this.flat.text,

			//Жилое помещение
			dwelling: this.dwelling.text,
			//Почтовый индекс
			postalIndex: this.postalIndex.text,

			fullAddress: ''
		};
		this.setFullAddress(result);
		return result;
	};

	/** Строковое представление адреса для simpleformat */
	valueToString = () => {
		let add = "";
		if (!GarDataModel.isEmptyVal(this.postalIndex.text)) {
			add += this.postalIndex.text;
			if (!GarDataModel.isEmptyVal(this.region.text)) {
				add += ', ';
			}
		}

		if (!GarDataModel.isEmptyVal(this.region.text))
			add += this.region.text;
		if (!GarDataModel.isEmptyVal(this.state.text))
			add += ', ' + this.state.text;
		if (!GarDataModel.isEmptyVal(this.city.text))
			add += ', ' + this.city.text;
		if (!GarDataModel.isEmptyVal(this.cityDistrict.text))
			add += ', ' + this.cityDistrict.text;
		if (!GarDataModel.isEmptyVal(this.street.text))
			add += ', ' + this.street.text;
		if (!GarDataModel.isEmptyVal(this.additionalTerritory.text))
			add += ', ' + this.additionalTerritory.text;
		if (!GarDataModel.isEmptyVal(this.streetOnAdditionalTerritory.text))
			add += ', ' + this.streetOnAdditionalTerritory.text;
		if (!GarDataModel.isEmptyVal(this.house.text))
			add += ', дом ' + this.house.text;
		if (!GarDataModel.isEmptyVal(this.corpus.text))
			add += ', корпус ' + this.corpus.text;
		if (!GarDataModel.isEmptyVal(this.building.text))
			add += ', строение ' + this.building.text;
		if (!GarDataModel.isEmptyVal(this.flat.text))
			add += ', кв./офис ' + this.flat.text;
		if (!GarDataModel.isEmptyVal(this.dwelling.text))
			add += ', пом. ' + this.dwelling.text;
		return add;
	};

	getFullAddress = ( data: GarAddressType ) => {
		var result = '';
		if (!GarDataModel.isEmptyVal(data.postalIndex)) {
			result += data.postalIndex;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.regionName)) {
			result += data.regionName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.stateName)) {
			result += data.stateName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.cityName)) {
			result += data.cityName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.cityDistrictName)) {
			result += data.cityDistrictName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.streetName)) {
			result += data.streetName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.additionalTerritoryName)) {
			result += data.additionalTerritoryName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.streetOnAdditionalTerritoryName)) {
			result += data.streetOnAdditionalTerritoryName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.house)) {
			result += "дом ";
			result += data.house;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.corpuse)) {
			result += "корпус ";
			result += data.corpuse;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.building)) {
			result += "строение ";
			result += data.building;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.flat)) {
			result += "кв./офис ";
			result += data.flat;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.dwelling)) {
			result += "пом. ";
			result += data.dwelling;
			result += ", ";
		}
		result = result.trim();
		if (result.lastIndexOf(',') == result.length - 1) {
			result = result.slice(0, result.length - 1);
		}

		data.fullAddress = result;
	};

	setFullAddress = ( data: GarAddressType ) => {
		var result = '';
		if (!GarDataModel.isEmptyVal(data.postalIndex)) {
			result += data.postalIndex;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.regionName)) {
			result += data.regionName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.stateName)) {
			result += data.stateName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.cityName)) {
			result += data.cityName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.cityDistrictName)) {
			result += data.cityDistrictName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.streetName)) {
			result += data.streetName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.additionalTerritoryName)) {
			result += data.additionalTerritoryName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.streetOnAdditionalTerritoryName)) {
			result += data.streetOnAdditionalTerritoryName;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.house)) {
			result += "дом ";
			result += data.house;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.corpuse)) {
			result += "корпус ";
			result += data.corpuse;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.building)) {
			result += "строение ";
			result += data.building;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.flat)) {
			result += "кв./офис ";
			result += data.flat;
			result += ", ";
		}
		if (!GarDataModel.isEmptyVal(data.dwelling)) {
			result += "пом. ";
			result += data.dwelling;
			result += ", ";
		}
		result = result.trim();
		if (result.lastIndexOf(',') == result.length - 1) {
			result = result.slice(0, result.length - 1);
		}
		data.fullAddress = result;
	};

	/** Выбран ли город федерального значения */
	isFederalCitySelected = () => {
		const baikonur = '63ed1a35-4be6-4564-a1ec-0c51f7383314';
		const moscow = '0c5b2444-70a0-4932-980c-b4dc0d3f02b5';
		const sanktPeterburg = 'c2deb16a-0330-4f05-821f-1d09c93331e6';
		const sevastopol = '6fdecb78-893a-4e3f-a5ba-aa062459463b';

		return [ baikonur, moscow, sanktPeterburg, sevastopol].includes( this.region.selectedAoGuid ?? "");
	};

	clear = () => {
		this.region.clear();
		this.state.clear();
		this.city.clear();
		this.cityDistrict.clear();
		this.street.clear();
		this.houseAo.clear();
		this.additionalTerritory.clear();
		this.streetOnAdditionalTerritory.clear();
		this.postalIndex.clear();
		this.house.clear();
		this.corpus.clear();
		this.building.clear();
		this.flat.clear();
		this.dwelling.clear();
	};

	/** валидация поля которому соответствует модель(model) */
	validateField = ( model: GarFieldDataModel ) => {
		if ( !this.validationEnabled )
			return true;
		let value = model.text;
		if ( model.code === GarFieldCodes.cityCode && (this.isFederalCitySelected() || this.state.shortName == 'г') ) {
			return true;
		}
		if ( model.code === GarFieldCodes.streetCode && this.houseAo.selectedAoGuid !== null) {
			return true;
		}
		if ( !model.required && model.isAutoCompleteAvailable && model.selectedAoGuid === null && value !== null && value !== '' ) {
			model.notValidError = 'Введены некорректные данные';
			model.notValid = true;
			return false;
		}
		const validator = model.validator;
		if ( model.selectedAoGuid !== null )
			value = model.selectedAoGuid;
		const validRes = validator.isValid(value);
		model.notValid = !validRes.isValid;
		model.notValidError = validRes.errorMessage;
		return validRes.isValid;
	};

	/** Валидация */
	isValid = () => {
		if ( !this.validationEnabled ) return true;
		let isValid = true;
		this.getFields().forEach( m => {
			if ( !this.validateField( m ) )
				isValid = false;
		});
		return isValid;
	};

	static isEmptyVal = ( val: string | null | undefined ) => val == null || val === '';
};

/** Модель данных ГАР-поля */
export class GarFieldDataModel {

	code: string;
	caption: string;
	visible: boolean;
	required: boolean;

	width: number;
	fieldName: string;
	readOnly: boolean;
	mask?: string;

	isAutoCompleteAvailable: boolean = false;
	kladrId: string | null = null;
	shortName: string | null = null;
	formalName: string | null = null;
	okato: string | null = null;
	oktmo: string | null = null;
	officialName: string | null = null;
	selectedAoGuid: string | null = null;
	regionCode: string | null = null;
	aoid: string | null = null;
	flatGuid: string | null = null;
	text: string | null = "";

	notValid: boolean = false;
	notValidError: string = "";
	
	validator: Validator;

	constructor( owner: GarDataModel, code: GarFieldCodes ) {

		const opt = GarFieldDataModel.getOptions( owner.visibilityMask, owner.requiredMask, code, owner.municipalSplit );
		this.code = code;
		this.caption = opt.caption;
		this.visible = opt.visible;
		this.required = opt.required;
		this.width = GarFieldWidth[code];
		this.fieldName = GarFieldNames[code];
		this.readOnly = !GarFieldEditable.includes(code);

		if ( code === GarFieldCodes.indexCode ) {
			this.mask = "999999";
		}

		//валидатор на обязательность
		if ( this.required ) {
			this.validator = ValidatorsFactory.getRequiredValidator();
		}
		else {
			this.validator = ValidatorsFactory.getComplexValidator({});
		}
	};

	/** Очистка значений модели */
	clear = () => {
		this.selectedAoGuid = null;
		this.aoid = null;
		this.text = null;
		this.kladrId = null;
		this.shortName = null;
		this.formalName = null;
		this.officialName = null;
		this.okato = null;
		this.oktmo = null;
		this.regionCode = null;
		this.flatGuid = null;
	};

	/**
	 * Возвращает объект описывающий опции поля
	 * @param visibilityMask Маска видимых полей
	 * @param requiredMask Маска обязательных полей
	 * @param code Код поля
	 * @returns Объект описывающий опции поля
	 */
	static getOptions = ( visibilityMask: string, requiredMask: string, code: GarFieldCodes, municipalSplit: boolean ): FieldOptionType => {
		let visible = false;
		if ( !visibilityMask ) {
			if ( ![GarFieldCodes.houseAoCode, GarFieldCodes.municipalCode, GarFieldCodes.settlementCode ].includes(code) ) {
				visible = true;
			}
		}
		else {
			// Инвертировать ли логику отображения.
			let invert = null;
			const firstChar = visibilityMask.substring(0, 1);
			if ( firstChar === '-' ) invert = true;
			if ( firstChar === '+' ) invert = false;
			if ( invert === null )
				throw new Error('Неверно указана маска видимости ФИАС контрола.');
			
			// Получаем значение видимости контрола.
			const isInMask = this.getVisibility( visibilityMask, code );
			visible = isInMask;
			if ( code === GarFieldCodes.houseAoCode ) {
				// Дом/строение виден только в случае его прямого добавления
				visible = invert === false && visible == true;
			}
			else {
				// Инвертируем его, если есть необходимость.
				visible = invert === true ? !visible : visible;
			}
			// Если муниципальное деление - прячем state, кроме прямого добавления
			if ( municipalSplit && code === GarFieldCodes.stateCode && !isInMask ) {
				visible = false;
			}
			// Если не муниципальное деление - прячем поля из него, кроме прямого добавления
			if ( !municipalSplit && [GarFieldCodes.municipalCode, GarFieldCodes.settlementCode].includes(code) && !isInMask ) {
				visible = false;
			}
		}

		const options = {
			caption: GarFieldCaptions[code],
			fieldName: GarFieldNames[code],
			width: GarFieldWidth[code],
			visible,
			required: this.getRequired( requiredMask, code ),
			code
		};

		return options;
	};

	/**
	 * Возвращает флаг отображения поля
	 * @param mask Маска
	 * @param code Код поля
	 * @returns флаг отображения поля
	 */
	static getVisibility = ( mask: string, code: GarFieldCodes ): boolean => {
		if ( mask ) {
			if ( !code ) {
				console.warn('Требуется указать параметр maskPart');
				return true;
			}
			return mask.indexOf(code) > -1;
		} 
		else
			return true;
	};

	/**
	 * Возвращает флаг необходимости заполнения
	 * @param mask Маска
	 * @param code Код поля
	 * @returns флаг необходимости заполнения
	 */
	static getRequired = ( mask: string, code: GarFieldCodes ): boolean => {
		if ( mask ) {
			if ( !code ) {
				console.warn('Требуется указать параметр maskPart');
				return false;
			}
			return mask.indexOf(code) > -1;
		} 
		else
			return false;
	};
};

/////////////////////////////////////////////////////////////////////////////////

enum GarFieldCodes {
	regionCode = 'R',
	stateCode = 'D',
	cityCode = 'T',
	microDistrictCode = 'M',
	streetCode = 'S',
	addTerCode = 'A',
	strAddTerCode = 'Q',
	houseCode = 'H',
	corpusCode = 'C',
	buildingCode = 'B',
	flatCode = 'F',
	dwellingCode = 'W',
	indexCode = 'P',
	houseAoCode = 'U',
	//Муниципальное деление
	municipalCode = 'X',
	settlementCode = 'Y'
};

const GarFieldCaptions = {
	[GarFieldCodes.regionCode]: 'Регион или область',
	[GarFieldCodes.stateCode]: 'Район',
	[GarFieldCodes.cityCode]: 'Город (Населенный пункт)',
	[GarFieldCodes.microDistrictCode]: 'Район города',
	[GarFieldCodes.streetCode]: 'Улица',
	[GarFieldCodes.addTerCode]: 'Доп. территория',
	[GarFieldCodes.strAddTerCode]: 'Улица на доп. территории',
	[GarFieldCodes.houseCode]: 'Дом',
	[GarFieldCodes.corpusCode]: 'Корпус',
	[GarFieldCodes.buildingCode]: 'Строение',
	[GarFieldCodes.flatCode]: 'Квартира',
	[GarFieldCodes.dwellingCode]: 'Жилое помещение',
	[GarFieldCodes.indexCode]: 'Индекс',
	[GarFieldCodes.houseAoCode]: 'Дом/строение',
	//Муниципальное деление
	[GarFieldCodes.municipalCode]: 'Муниципальный район/округ',
	[GarFieldCodes.settlementCode]: 'Населенный пункт'
};

const GarFieldNames = {
	[GarFieldCodes.regionCode]: 'region',
	[GarFieldCodes.stateCode]: 'state',
	[GarFieldCodes.cityCode]: 'city',
	[GarFieldCodes.microDistrictCode]: 'microDistrict',
	[GarFieldCodes.streetCode]: 'street',
	[GarFieldCodes.addTerCode]: 'addTer',
	[GarFieldCodes.strAddTerCode]: 'strAddTer',
	[GarFieldCodes.houseCode]: 'house',
	[GarFieldCodes.corpusCode]: 'corpus',
	[GarFieldCodes.buildingCode]: 'building',
	[GarFieldCodes.flatCode]: 'flat',
	[GarFieldCodes.dwellingCode]: 'dwelling',
	[GarFieldCodes.indexCode]: 'index',
	[GarFieldCodes.houseAoCode]: 'houseAo',
	//Муниципальное деление
	[GarFieldCodes.municipalCode]: 'municipal',
	[GarFieldCodes.settlementCode]: 'settlement'
};

const GarFieldWidth = {
	[GarFieldCodes.regionCode]: 6,
	[GarFieldCodes.stateCode]: 6,
	[GarFieldCodes.cityCode]: 6,
	[GarFieldCodes.microDistrictCode]: 6,
	[GarFieldCodes.streetCode]: 4,
	[GarFieldCodes.addTerCode]: 4,
	[GarFieldCodes.strAddTerCode]: 4,
	[GarFieldCodes.houseCode]: 2,
	[GarFieldCodes.corpusCode]: 2,
	[GarFieldCodes.buildingCode]: 2,
	[GarFieldCodes.flatCode]: 2,
	[GarFieldCodes.dwellingCode]: 2,
	[GarFieldCodes.indexCode]: 2,
	[GarFieldCodes.houseAoCode]: 3,
	//Муниципальное деление
	[GarFieldCodes.municipalCode]: 6,
	[GarFieldCodes.settlementCode]: 6
};

const GarFieldEditable = [
	GarFieldCodes.indexCode, GarFieldCodes.houseCode,
	GarFieldCodes.corpusCode, GarFieldCodes.buildingCode,
	GarFieldCodes.dwellingCode, GarFieldCodes.flatCode
];
